import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ServerService} from './ServerService';

export interface FormularioImplantacao {
  nomeEmpresa: string;
  responsavelNome: string;
  responsavelWhatsapp: string;
  whatsappLoja: string;
  emailAcesso?: string;
  endereco: {cep: string; logradouro: string; numero?: string; semNumero?: boolean; bairro: string; cidade: string; uf: string; complemento?: string};
  fusoHorario: string;
  horarios: Array<{diaSemana: number; abre: string; fecha: string}>;
  modalidades: string[];
  formasPagamento: string[];
  cardapioUrl?: string;
  linkIfood?: string;
  arquivosCardapio: number[];
  entrega?: {area: string; taxa: string; prazo: string; pedidoMinimo?: string};
  mesasQr?: boolean;
  qtdeMesas?: number;
  fidelidade?: {tipos: string[]; regraSelos?: string; regraPontos?: string; quantidadeResgate?: string; brinde?: string; percentualCashback?: number};
  integracao?: string;
  instagram?: string;
  saudacao?: string;
  observacoes?: string;
  participante?: string;
  computador?: string;
  sistemaOperacional?: string;
  impressora?: string;
}

export interface VagaImplantacao {
  inicio: string;
  fim: string;
  implantador: {id: number; nome?: string};
}

export interface ImplantacaoDTO {
  id: number; versao: number; etapa: string;
  empresa: {id: number; nome: string; dominio?: string};
  fatura: {id: number};
  responsavel: {id: number; nome: string} | null;
  formulario: {status: string; dados: FormularioImplantacao | null; anexos: Array<{id: number; nomeOriginal: string; tipo: string; mime: string; tamanho: number}>};
  formularioUrl: string;
  recebidoEm: string | null; prazoCadastro: string | null; dataMinimaTreinamento: string | null;
  proximaAcao: string; proximaAcaoEm: string | null; bloqueio: string | null;
  tarefas: Array<{chave: string; titulo: string; grupo: string; obrigatoria: boolean; automatica: boolean; status: string; observacao?: string}>;
  agendamento: {id: number; implantador: {id: number; nome: string}; inicio: string; fim: string; status: string; link?: string} | null;
  grupo: {chatId?: string; nome?: string; conviteUrl?: string; status?: string};
  automacaoGrupo?: {id: number; status: string; ordem: number; erro?: string; revisaoUrl: string};
  eventos: any[]; alertas: string[];
}

@Injectable({providedIn: 'root'})
export class ImplantacoesService extends ServerService {
  private readonly endpoint = '/suporte-implantacoes';

  constructor(http: HttpClient) { super(http); }

  liste(filtros: any = {}): Promise<ImplantacaoDTO[]> { return this.obtenha(this.endpoint, filtros); }
  equipe(): Promise<any[]> { return this.obtenha(`${this.endpoint}/equipe`, {}); }
  agenda(de: string, ate: string): Promise<any[]> { return this.obtenha(`${this.endpoint}/agenda`, {de, ate}); }
  feriados(): Promise<any[]> { return this.obtenha(`${this.endpoint}/feriados`, {}); }
  crieFeriado(data: string, descricao: string): Promise<any> { return this.facaPost(`${this.endpoint}/feriados`, {data, descricao}); }
  removaFeriado(id: number): Promise<any> { return this.remova(`${this.endpoint}/feriados/${id}`, {}); }
  obtenhaImplantacao(id: number): Promise<ImplantacaoDTO> { return this.obtenha(`${this.endpoint}/${id}`, {}); }
  acompanhe(id: number, dados: {versao: number; proximaAcao: string; proximaAcaoEm: string | null; bloqueio: string | null}): Promise<ImplantacaoDTO> {
    return this.http.patch(`${this.endpoint}/${id}`, dados).toPromise().then(this.retorno).catch(this.handleError);
  }
  mudeEtapa(id: number, versao: number, etapa: string, motivo: string): Promise<ImplantacaoDTO> {
    return this.facaPost(`${this.endpoint}/${id}/etapa`, {versao, etapa, motivo});
  }
  atualizeTarefa(id: number, chave: string, versao: number, status: string, observacao: string): Promise<ImplantacaoDTO> {
    return this.facaPost(`${this.endpoint}/${id}/tarefas/${encodeURIComponent(chave)}`, {versao, status, observacao});
  }
  transfira(id: number, versao: number, usuarioId: number, motivo: string): Promise<ImplantacaoDTO> {
    return this.facaPost(`${this.endpoint}/${id}/responsavel`, {versao, usuarioId, motivo});
  }
  vagas(id: number, data: string): Promise<VagaImplantacao[]> { return this.obtenha(`${this.endpoint}/${id}/vagas`, {data}); }
  reserve(id: number, implantadorId: number, inicio: string, link?: string): Promise<ImplantacaoDTO> {
    return this.facaPost(`${this.endpoint}/${id}/agendamento`, {implantadorId, inicio, link});
  }
  resultadoReuniao(id: number, status: string, motivo?: string, link?: string): Promise<ImplantacaoDTO> {
    return this.facaPost(`${this.endpoint}/${id}/agendamento/status`, {status, motivo, link});
  }
  reprocesse(id: number): Promise<any> { return this.facaPost(`${this.endpoint}/${id}/reprocessar`, {}); }

  publicaObtenha(token: string): Promise<any> { return this.obtenha(`${this.endpoint}/publica/${encodeURIComponent(token)}`, {}); }
  publiqueFormulario(token: string, dados: FormularioImplantacao, enviar: boolean): Promise<any> {
    return this.http.put(`${this.endpoint}/publica/${encodeURIComponent(token)}/formulario`, {dados, enviar})
      .toPromise().then(this.retorno).catch(this.handleError);
  }
  publiqueAnexos(token: string, arquivos: File[], tipo: string): Promise<any> {
    const form = new FormData();
    arquivos.forEach(arquivo => form.append('arquivos', arquivo, arquivo.name));
    form.append('tipo', tipo);
    return this.facaPost(`${this.endpoint}/publica/${encodeURIComponent(token)}/anexos`, form);
  }
  urlAnexo(token: string, anexoId: number): string { return `${this.endpoint}/publica/${encodeURIComponent(token)}/anexos/${anexoId}`; }
  vagasPublicas(token: string, data: string): Promise<VagaImplantacao[]> {
    return this.obtenha(`${this.endpoint}/publica/${encodeURIComponent(token)}/vagas`, {data});
  }
  reservaPublica(token: string, implantadorId: number, inicio: string): Promise<any> {
    return this.facaPost(`${this.endpoint}/publica/${encodeURIComponent(token)}/agendamento`, {implantadorId, inicio});
  }
}
